import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NumberFormat from 'react-number-format';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import '../../../styles/signup.css';

const ProfileTab = ({ user, onProfileSaved }) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [marketingSmsAllowed, setMarketingSmsAllowed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [personalDataMessage, setPersonalDataMessage] = useState(null);
  const [editingPersonalData, setEditingPersonalData] = useState(false);
  const [savingPersonalData, setSavingPersonalData] = useState(false);

  // Зміна номера телефону
  const [phoneChangeNewPhone, setPhoneChangeNewPhone] = useState('');
  const [phoneChangeCode, setPhoneChangeCode] = useState('');
  const [phoneChangeStep, setPhoneChangeStep] = useState('idle'); // 'idle' | 'code_sent'
  const [phoneChangeLoading, setPhoneChangeLoading] = useState(false);
  const [phoneChangeError, setPhoneChangeError] = useState('');
  const [phoneChangeSuccess, setPhoneChangeSuccess] = useState('');
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [contactDataMessage, setContactDataMessage] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name ?? '');
      setSurname(user.surname ?? '');
      setBirthDate(user.birthDate ? String(user.birthDate).slice(0, 10) : '');
      setEmailAddress(user.emailAddress?.trim() ?? '');
      setMarketingSmsAllowed(Boolean(user.marketingSmsAllowed));
    }
  }, [user]);

  useEffect(() => {
    if (editingEmail) setEmailEditValue(user?.emailAddress?.trim() ?? '');
  }, [editingEmail, user?.emailAddress]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    setMessage(null);
    axios
      .put(
        '/api/v1/user/profile',
        { name: name || null, surname: surname || null, marketingSmsAllowed },
        { headers: { Authorization: token } }
      )
      .then(() => {
        setMessage({ type: 'success', text: 'Налаштування сповіщень оновлено' });
        setEditingPersonalData(false);
        if (typeof onProfileSaved === 'function') onProfileSaved();
      })
      .catch(() => {
        setMessage({ type: 'danger', text: 'Не вдалося зберегти' });
      })
      .finally(() => setSaving(false));
  };

  const handleSavePersonalData = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      setPersonalDataMessage({ type: 'danger', text: 'Необхідно увійти в акаунт' });
      return;
    }
    setPersonalDataMessage(null);
    setSavingPersonalData(true);
    const birthDateTrimmed = birthDate.trim();
    const hasBirthDateAlreadySet = Boolean(user?.birthDate);
    const payload = {
      name: name || null,
      surname: surname || null,
      marketingSmsAllowed: Boolean(user?.marketingSmsAllowed),
    };
    if (!hasBirthDateAlreadySet && birthDateTrimmed) {
      payload.birthDate = birthDateTrimmed;
    }
    axios
      .put('/api/v1/user/profile', payload, { headers: { Authorization: token } })
      .then(() => {
        setPersonalDataMessage({ type: 'success', text: 'Особисті дані оновлено' });
        setEditingPersonalData(false);
        if (typeof onProfileSaved === 'function') onProfileSaved();
      })
      .catch((err) => {
        const msg =
          err.response?.status === 401
            ? 'Сесію завершено. Увійдіть знову.'
            : err.response?.data?.errorMessage || err.response?.data?.message || 'Не вдалося зберегти дані';
        setPersonalDataMessage({ type: 'danger', text: msg });
      })
      .finally(() => setSavingPersonalData(false));
  };

  const cancelEditPersonalData = () => {
    setName(user?.name ?? '');
    setSurname(user?.surname ?? '');
    setBirthDate(user?.birthDate ? String(user.birthDate).slice(0, 10) : '');
    setEditingPersonalData(false);
  };

  const startEditEmail = () => {
    setContactDataMessage(null);
    setEmailEditValue(user?.emailAddress?.trim() ?? '');
    setEditingEmail(true);
  };

  const cancelEditEmail = () => {
    setEditingEmail(false);
    setEmailEditValue(user?.emailAddress?.trim() ?? '');
  };

  const handleSaveEmail = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setContactDataMessage(null);
    setSavingEmail(true);
    const value = emailEditValue.trim() || null;
    axios
      .put('/api/v1/user/profile', { emailAddress: value }, { headers: { Authorization: token } })
      .then(() => {
        setEmailAddress(value ?? '');
        setContactDataMessage({ type: 'success', text: 'Імейл оновлено' });
        setEditingEmail(false);
        if (typeof onProfileSaved === 'function') onProfileSaved();
      })
      .catch((err) => {
        const msg = err.response?.data?.errorMessage || err.response?.data?.message || 'Не вдалося зберегти імейл';
        setContactDataMessage({ type: 'danger', text: msg });
      })
      .finally(() => setSavingEmail(false));
  };

  const formatPhone = (p) => {
    if (!p) return '—';
    const digits = (p + '').replace(/\D/g, '');
    if (digits.length >= 9) return '0' + digits.slice(-9);
    return p;
  };

  const normalizePhoneForApi = (raw) => {
    const digits = (raw + '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('38')) return digits.slice(2);
    return digits;
  };

  const handleRequestPhoneChange = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    const newPhone = normalizePhoneForApi(phoneChangeNewPhone);
    if (newPhone.length !== 10) {
      setPhoneChangeError('Введіть коректний номер (10 цифр)');
      return;
    }
    setPhoneChangeError('');
    setPhoneChangeSuccess('');
    setPhoneChangeLoading(true);
    axios
      .post('/api/v1/user/profile/phone/request-change', { newPhoneNumber: newPhone }, { headers: { Authorization: token } })
      .then((res) => {
        setPhoneChangeStep('code_sent');
        setPhoneChangeCode('');
        setPhoneChangeSuccess(res.data?.data || 'Код надіслано на вказаний номер');
      })
      .catch((err) => {
        const msg = err.response?.data?.errorMessage || err.response?.data?.message || 'Не вдалося надіслати код';
        setPhoneChangeError(msg);
      })
      .finally(() => setPhoneChangeLoading(false));
  };

  const handleConfirmPhoneChange = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    const newPhone = normalizePhoneForApi(phoneChangeNewPhone);
    const code = (phoneChangeCode + '').replace(/\D/g, '');
    if (newPhone.length !== 10 || code.length !== 6) {
      setPhoneChangeError('Введіть номер та 6-значний код');
      return;
    }
    setPhoneChangeError('');
    setPhoneChangeLoading(true);
    axios
      .post('/api/v1/user/profile/phone/confirm-change', { newPhoneNumber: newPhone, confirmationCode: code }, { headers: { Authorization: token } })
      .then(() => {
        setContactDataMessage({ type: 'success', text: 'Номер телефону успішно змінено' });
        setPhoneChangeSuccess('Номер телефону успішно змінено');
        setPhoneChangeStep('idle');
        setPhoneChangeNewPhone('');
        setPhoneChangeCode('');
        setShowPhoneModal(false);
        if (typeof onProfileSaved === 'function') onProfileSaved();
      })
      .catch((err) => {
        const msg = err.response?.data?.errorMessage || err.response?.data?.message || 'Не вдалося змінити номер';
        setPhoneChangeError(msg);
      })
      .finally(() => setPhoneChangeLoading(false));
  };

  const cancelPhoneChange = () => {
    setPhoneChangeStep('idle');
    setPhoneChangeNewPhone('');
    setPhoneChangeCode('');
    setPhoneChangeError('');
    setPhoneChangeSuccess('');
  };

  const openPhoneModal = () => {
    setContactDataMessage(null);
    setPhoneChangeError('');
    setPhoneChangeSuccess('');
    setPhoneChangeStep('idle');
    setShowPhoneModal(true);
  };

  const closePhoneModal = () => {
    setShowPhoneModal(false);
    cancelPhoneChange();
  };

  const initialSms = Boolean(user?.marketingSmsAllowed);
  const hasChanges = marketingSmsAllowed !== initialSms;

  const emailDisplay = user?.emailAddress?.trim() ? user.emailAddress.trim() : 'Не задано';

  const formatBirthDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const birthDateDisplay = user?.birthDate ? formatBirthDate(user.birthDate) : null;

  return (
    <div className="personal-cabinet-profile personal-cabinet-tab-content">
      <h5 className="font-weight-bold mb-3 text-center">Профіль</h5>

      <div className="row">
        {/* Блок: Особисті дані */}
        <div className="col-12 col-md-6 mb-3">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex align-items-center flex-wrap mb-2">
            <h5 className="font-weight-bold mb-0 d-flex align-items-center" style={{ fontSize: '1.2rem' }}>
              <span className="mr-1" aria-hidden="true">👤</span>
              Особисті дані
              {!editingPersonalData && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ml-1 text-dark"
                  onClick={() => setEditingPersonalData(true)}
                  title="Редагувати ім'я та прізвище"
                  aria-label="Редагувати ім'я та прізвище"
                >
                  <i className="bi bi-pencil-fill" style={{ fontSize: '0.9rem' }} />
                </button>
              )}
            </h5>
          </div>
          {!editingPersonalData ? (
            <div className="personal-cabinet-profile-field">
              <p className="mb-1"><span className="font-weight-bold">Ім'я:</span> {name ? name : '—'}</p>
              <p className="mb-1"><span className="font-weight-bold">Прізвище:</span> {surname ? surname : '—'}</p>
              <p className="mb-0">
                <span className="font-weight-bold">День народження:</span> {birthDateDisplay || 'Не встановлено'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSavePersonalData}>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <div className="form-group mb-2">
                    <label className="font-weight-bold small">Ім'я</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Введіть ім'я"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="form-group mb-2">
                    <label className="font-weight-bold small">Прізвище</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Введіть прізвище"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="form-group mb-2">
                    <label className="font-weight-bold small">День народження</label>
                    {user?.birthDate ? (
                      <p className="mb-0">
                        {birthDateDisplay}
                        <OverlayTrigger placement="top" overlay={<Tooltip id="profile-birthdate-tooltip"><strong>Зміна дати народження лише через підтримку (напишіть нам у Telegram)</strong></Tooltip>}>
                          <i className="bi bi-info-circle px-1" />
                        </OverlayTrigger>
                      </p>
                    ) : (
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap">
                <button type="submit" className="btn btn-dark mr-2" disabled={savingPersonalData}>
                  {savingPersonalData ? 'Збереження…' : 'Зберегти'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={cancelEditPersonalData} disabled={savingPersonalData}>
                  Скасувати
                </button>
              </div>
              {personalDataMessage && (
                <div className={`alert alert-${personalDataMessage.type} mt-2 mb-0 py-2`} role="alert">
                  {personalDataMessage.text}
                </div>
              )}
            </form>
          )}
          {!editingPersonalData && personalDataMessage && (
            <div className={`alert alert-${personalDataMessage.type} mt-2 mb-0 py-2`} role="alert">
              {personalDataMessage.text}
            </div>
          )}
        </div>
      </div>
        </div>

        {/* Блок: Контактні дані */}
        <div className="col-12 col-md-6 mb-3">
      <div className="card h-100">
        <div className="card-body">
          <h5 className="font-weight-bold mb-2 d-flex align-items-center" style={{ fontSize: '1.2rem' }}>
            <span className="mr-1" aria-hidden="true">📱</span>
            Контактні дані
          </h5>
          <p className="mb-2 personal-cabinet-profile-field d-flex align-items-center flex-wrap">
            <span className="font-weight-bold mr-1">Номер телефону:</span>
            <span className="mr-1">{formatPhone(user?.phoneNumber)}</span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-dark"
              onClick={openPhoneModal}
              title="Змінити номер телефону"
              aria-label="Змінити номер телефону"
            >
              <i className="bi bi-pencil-fill" style={{ fontSize: '0.9rem' }} />
            </button>
          </p>

          {!editingEmail ? (
            <p className="mb-0 personal-cabinet-profile-field d-flex align-items-center flex-wrap">
              <span className="font-weight-bold mr-1">Імейл:</span>
              <span className="mr-1">{emailDisplay}</span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-dark"
                onClick={startEditEmail}
                title="Змінити імейл"
                aria-label="Змінити імейл"
              >
                <i className="bi bi-pencil-fill" style={{ fontSize: '0.9rem' }} />
              </button>
            </p>
          ) : (
            <form onSubmit={handleSaveEmail} className="mb-0">
              <div className="form-group mb-2">
                <label className="font-weight-bold small mr-1">Імейл:</label>
                <input
                  type="email"
                  className="form-control form-control-sm d-inline-block"
                  placeholder="email@example.com"
                  value={emailEditValue}
                  onChange={(e) => setEmailEditValue(e.target.value)}
                  style={{ maxWidth: '240px' }}
                  autoFocus
                />
              </div>
              <div className="d-flex flex-wrap">
                <button type="submit" className="btn btn-dark btn-sm mr-2" disabled={savingEmail}>
                  {savingEmail ? 'Збереження…' : 'Зберегти'}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={cancelEditEmail} disabled={savingEmail}>
                  Скасувати
                </button>
              </div>
            </form>
          )}
          {contactDataMessage && (
            <div className={`alert alert-${contactDataMessage.type} mt-2 mb-0 py-2`} role="alert">
              {contactDataMessage.text}
            </div>
          )}
        </div>
      </div>
        </div>

        {/* Модалка: зміна номера телефону — Bootstrap стилі */}
        {showPhoneModal && (
          <div className="modal-backdrop show" style={{ display: 'block' }} onClick={closePhoneModal} aria-hidden="true" />
        )}
        <div
          className={`modal fade ${showPhoneModal ? 'show' : ''}`}
          style={{ display: showPhoneModal ? 'block' : 'none' }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="phoneModalTitle"
          onClick={closePhoneModal}
        >
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header py-3 px-3 d-flex align-items-center">
                <div className="flex-grow-1 text-center">
                  <h5 className="modal-title mb-0 font-weight-bold" id="phoneModalTitle">Змінити номер телефону</h5>
                </div>
                <button type="button" className="close py-0 my-0 ml-auto" onClick={closePhoneModal} aria-label="Закрити">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body pt-2">
                {phoneChangeStep === 'idle' && (
                  <>
                    {phoneChangeSuccess && <div className="alert alert-success small py-2 mb-2">{phoneChangeSuccess}</div>}
                    <form onSubmit={handleRequestPhoneChange}>
                      <div className="form-group mb-2">
                        <label className="font-weight-bold">Введіть новий номер:</label>
                        <NumberFormat
                          className="form-control"
                          placeholder="0501234567"
                          format="(###) ###-####"
                          mask="_"
                          value={phoneChangeNewPhone}
                          onValueChange={({ value }) => setPhoneChangeNewPhone(value)}
                        />
                      </div>
                      <button type="submit" className="btn btn-dark mt-2" disabled={phoneChangeLoading}>
                        {phoneChangeLoading ? 'Відправка…' : 'Надіслати код'}
                      </button>
                    </form>
                  </>
                )}
                {phoneChangeStep === 'code_sent' && (
                  <form onSubmit={handleConfirmPhoneChange}>
                    <p className="text-success font-weight-bold mb-2">{phoneChangeSuccess}</p>
                    <div className="form-group mb-2">
                      <label className="font-weight-bold">Введіть код з SMS (6 цифр):</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="123456"
                        maxLength={6}
                        value={phoneChangeCode}
                        onChange={(e) => setPhoneChangeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoFocus
                      />
                    </div>
                    <div className="d-flex flex-wrap mt-2">
                      <button type="submit" className="btn btn-dark mr-2" disabled={phoneChangeLoading || phoneChangeCode.length !== 6}>
                        {phoneChangeLoading ? 'Збереження…' : 'Підтвердити зміну'}
                      </button>
                      <button type="button" className="btn btn-outline-secondary" onClick={cancelPhoneChange} disabled={phoneChangeLoading}>
                        Скасувати
                      </button>
                    </div>
                  </form>
                )}
                {phoneChangeError && <div className="alert alert-danger small mt-2 mb-0 py-2">{phoneChangeError}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Блок: Сповіщення — тимчасово закоментовано
        <div className="col-12 col-md-6 mb-3">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="font-weight-bold mb-3">Сповіщення</h6>
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3 mb-0">
          <div className="signup-form">
            <div className="form-group form-group-signup-sms">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="profile-sms-allowed"
                  checked={marketingSmsAllowed}
                  onChange={(e) => setMarketingSmsAllowed(e.target.checked)}
                  style={{ accentColor: '#212529' }}
                  aria-label="Отримувати персональні SMS"
                />
                <span className="form-check-label signup-sms-offers-label mb-0">
                  Отримувати персональні SMS. Відписка доступна у будь-який момент.
                  <OverlayTrigger placement="top" overlay={<Tooltip id="profile-sms-tooltip"><strong>Ми надсилатимемо SMS з інформацією про акції, нові надходження та спеціальні пропозиції. Не більше 2-4 в місяць.</strong></Tooltip>}>
                    <i className="bi bi-info-circle px-1" />
                  </OverlayTrigger>
                </span>
              </div>
            </div>
          </div>
        </div>
        {message && (
          <div className={`alert alert-${message.type} mb-2`} role="alert">
            {message.text}
          </div>
        )}
        <button type="submit" className="btn btn-dark" disabled={saving || !hasChanges}>
          {saving ? 'Збереження...' : 'Зберегти зміни'}
        </button>
      </form>
        </div>
      </div>
        </div>
        */}
      </div>
    </div>
  );
};

export default ProfileTab;
